import { useState, useCallback, useEffect } from "react";
import { ScrollView, RefreshControl, View, Text } from "react-native";
import { useRouter } from "expo-router";
import { collection, collectionGroup, query, where, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import ContentWidth from "@/components/ContentWidth";
import { FinancesSkeleton } from "@/components/Skeleton";
import { colors, radius, font } from "@/lib/theme";
import type { Fee, Member } from "@/lib/types";
import styled from "styled-components/native";

const Section = styled.View`
  padding: 20px 20px 0;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const Card = styled.View`
  margin: 0 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  overflow: hidden;
`;

const PlayerRow = styled.Pressable<{ hasBorder?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding: 14px 16px;
  ${(p) => p.hasBorder && `border-bottom-width: 1px; border-bottom-color: ${colors.line};`}
`;

const Avatar = styled.View`
  width: 38px;
  height: 38px;
  border-radius: 19px;
  background-color: ${colors.amber50};
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.amber200};
`;

const AvatarText = styled.Text`
  font-size: 15px;
  font-weight: ${font.bold};
  color: ${colors.amber};
`;

const RowMain = styled.View`
  flex: 1;
  margin-left: 12px;
`;

const RowName = styled.Text`
  font-size: 15px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const RowSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  margin-top: 2px;
`;

const RowAmount = styled.Text`
  font-size: 15px;
  font-weight: ${font.extrabold};
  color: ${colors.amber};
  margin-right: 10px;
`;

const RemindBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.sm}px;
  padding: 7px 14px;
`;

const RemindBtnText = styled.Text`
  color: #fff;
  font-size: 12px;
  font-weight: ${font.bold};
`;

const TotalCard = styled.View`
  background-color: ${colors.amber50};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.amber200};
  padding: 16px;
  margin: 20px 20px 0;
`;

const TotalLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.amber};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TotalAmount = styled.Text`
  font-size: 30px;
  font-weight: ${font.extrabold};
  color: ${colors.amber};
  margin-top: 4px;
  letter-spacing: -0.5px;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
  padding: 32px 20px;
`;

function pence(p: number): string {
  return `\u00A3${(p / 100).toFixed(2)}`;
}

interface OutstandingPlayer {
  userId: string;
  displayName: string;
  email: string;
  unpaidFees: number;
  unpaidCount: number;
  membershipOwed: number;
  total: number;
}

export default function FinancesOutstandingScreen() {
  const router = useRouter();
  const { club } = useClub();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [players, setPlayers] = useState<OutstandingPlayer[]>([]);

  const loadData = useCallback(async () => {
    if (!club) return;
    try {
      const feeQuery = query(collectionGroup(db, "fees"), where("clubId", "==", club.id));
      const feeSnap = await getDocs(feeQuery);
      const fees = feeSnap.docs.map((d) => d.data()) as Fee[];

      const memberSnap = await getDocs(collection(db, "clubs", club.id, "members"));
      const members = memberSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as (Member & { id: string })[];

      const playerMap = new Map<string, OutstandingPlayer>();
      for (const m of members) {
        playerMap.set(m.userId, {
          userId: m.userId,
          displayName: m.displayName,
          email: m.email,
          unpaidFees: 0,
          unpaidCount: 0,
          membershipOwed: m.membership ? Math.max(0, m.membership.totalAmount - m.membership.paidAmount) : 0,
          total: 0,
        });
      }
      for (const f of fees) {
        if (f.paid) continue;
        const p = playerMap.get(f.userId);
        if (p) {
          p.unpaidFees += f.amount;
          p.unpaidCount += 1;
        }
      }
      const list = Array.from(playerMap.values())
        .map((p) => ({ ...p, total: p.unpaidFees + p.membershipOwed }))
        .filter((p) => p.total > 0)
        .sort((a, b) => b.total - a.total);

      setPlayers(list);
    } catch (err) {
      console.error("Failed to load outstanding:", err);
    }
  }, [club?.id]);

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg }}>
        <FinancesSkeleton />
      </View>
    );
  }

  const total = players.reduce((s, p) => s + p.total, 0);

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.bg }}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.brand} />}
    >
      <ContentWidth>
        <TotalCard>
          <TotalLabel>Total outstanding</TotalLabel>
          <TotalAmount>{pence(total)}</TotalAmount>
          <Text style={{ fontSize: 13, color: colors.amber, fontWeight: "600", marginTop: 4 }}>
            from {players.length} {players.length === 1 ? "member" : "members"}
          </Text>
        </TotalCard>

        <Section>
          <SectionLabel>Members with outstanding</SectionLabel>
        </Section>

        {players.length === 0 ? (
          <Card>
            <EmptyText>🎉 Everyone's all paid up</EmptyText>
          </Card>
        ) : (
          <Card>
            {players.map((p, i) => (
              <PlayerRow
                key={p.userId}
                hasBorder={i < players.length - 1}
                onPress={() => router.push(`/(app)/member-detail?userId=${p.userId}`)}
              >
                <Avatar>
                  <AvatarText>{p.displayName?.[0]?.toUpperCase() || "?"}</AvatarText>
                </Avatar>
                <RowMain>
                  <RowName>{p.displayName}</RowName>
                  <RowSub>
                    {p.unpaidCount > 0 && `${p.unpaidCount} match fee${p.unpaidCount > 1 ? "s" : ""}`}
                    {p.unpaidCount > 0 && p.membershipOwed > 0 && " · "}
                    {p.membershipOwed > 0 && `${pence(p.membershipOwed)} membership`}
                  </RowSub>
                </RowMain>
                <RowAmount>{pence(p.total)}</RowAmount>
                <Text style={{ fontSize: 18, color: colors.inkMuted }}>›</Text>
              </PlayerRow>
            ))}
          </Card>
        )}

        <View style={{ height: 40 }} />
      </ContentWidth>
    </ScrollView>
  );
}
